import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { IPublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../config/authConfig";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

class ApiClient {
  private client: AxiosInstance;
  private msalInstance: IPublicClientApplication | null = null;
  private isRefreshing = false;
  private refreshQueue: Array<(token: string | null) => void> = [];

  constructor(baseURL: string = API_BASE_URL) {
    this.client = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  setMsalInstance(instance: IPublicClientApplication): void {
    this.msalInstance = instance;
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      async (config) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(this.handleError(error))
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Only retry 401 once per request
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // If already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.refreshQueue.push((token: string | null) => {
                if (token) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(this.client(originalRequest));
                } else {
                  reject(this.handleError(error));
                }
              });
            });
          }

          // Mark as refreshing
          this.isRefreshing = true;

          try {
            console.log("Token expired, refreshing...");
            const newToken = await this.getAuthToken(true);

            if (newToken) {
              // Update original request
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              // Process queued requests
              this.refreshQueue.forEach((callback) => callback(newToken));
              this.refreshQueue = [];

              return this.client(originalRequest);
            } else {
              // Failed to refresh - clear queue
              this.refreshQueue.forEach((callback) => callback(null));
              this.refreshQueue = [];
              return Promise.reject(this.handleError(error));
            }
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private async getAuthToken(forceRefresh = false): Promise<string | null> {
    if (!this.msalInstance) {
      console.warn("MSAL instance not set in apiClient");
      return null;
    }

    try {
      const account = this.msalInstance.getActiveAccount();
      if (!account) {
        console.warn("No active account found");
        return null;
      }

      // Request ID token instead of access token for backend validation
      // ID token has the right audience (your client ID)
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: ["openid", "profile", "email"],
        account,
        forceRefresh, // Force refresh if token expired
      });

      // Use ID token instead of access token - it has aud: your-client-id
      return response.idToken;
    } catch (error) {
      console.error("Failed to acquire token:", error);
      return null;
    }
  }

  private handleError(error: any): ApiError {
    if (axios.isAxiosError(error)) {
      return {
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        code: error.code,
      };
    }
    return {
      message: error.message || "An unexpected error occurred",
    };
  }

  async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<{ success: boolean; data?: T; message?: string }> {
    const response = await this.client.get(url, config);
    return response.data;
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<{ success: boolean; data?: T; message?: string }> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<{ success: boolean; data?: T; message?: string }> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<{ success: boolean; data?: T; message?: string }> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<{ success: boolean; data?: T; message?: string }> {
    const response = await this.client.delete(url, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();
